import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import {
  LogoutIcon,
  SettingsIcon,
  SwapIcon
} from '@/components/ui/Icons/Icons';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { Swipeable } from '@/components/ui/Swipeable/Swipeable';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { PROFILE_MODES, ROUTES } from '@/constants/common';
import { useBelow } from '@/hooks/useBelow';
import { useSignOut } from '@/hooks/useSignOut';
import { useProfileMode } from '@/providers/ProfileModeProvider/ProfileModeProvider';

import * as S from './ProfileMenuMobile.styles';

export const ProfileMenuMobile = () => {
  const isMobile = useBelow('tablet');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const router = useRouter();
  const path = usePathname();
  const isProfilePage = path.startsWith(ROUTES.PROFILE);

  const handleSignout = useSignOut();
  const { profileMode, switchProfileMode, canSwitchProfileMode } =
    useProfileMode();

  const { t } = useTranslation();

  if (!isMobile) return null;

  const handleTogglePopup = () => setIsPopupOpen(prev => !prev);

  const handleToggleModal = () => setIsModalOpen(prev => !prev);

  const handleModalOpen = () => {
    handleTogglePopup();
    handleToggleModal();
  };

  const handleSwitchProfile = () => {
    const targetRoute =
      profileMode === PROFILE_MODES.COMPANY
        ? ROUTES.PROFILE
        : ROUTES.COMPANY_PROFILE;
    switchProfileMode();
    router.push(targetRoute);
    handleTogglePopup();
  };

  return (
    <>
      {isProfilePage && (
        <S.ToggleButton onClick={handleTogglePopup}>
          <SettingsIcon />
        </S.ToggleButton>
      )}

      {isPopupOpen && <Overlay zIndex={50} onClick={handleTogglePopup} />}
      <Swipeable onSwipeDown={handleTogglePopup}>
        <S.Popup $isOpen={isPopupOpen}>
          <S.PopupTitle variant="h4">
            {t('ProfileMenu.settings_label')}
          </S.PopupTitle>

          {canSwitchProfileMode && (
            <S.PopupItem onClick={handleSwitchProfile}>
              <SwapIcon />
              <Typography variant="body2">
                {profileMode === PROFILE_MODES.COMPANY
                  ? t('ProfileMenu.switch_on_candidate_profile_label')
                  : t('ProfileMenu.switch_on_company_profile_label')}
              </Typography>
            </S.PopupItem>
          )}

          <S.PopupItemLogout onClick={handleModalOpen}>
            <LogoutIcon />
            <Typography variant="body2">
              {t('ProfileMenu.signout_button_label')}
            </Typography>
          </S.PopupItemLogout>
        </S.Popup>
      </Swipeable>

      {isModalOpen && (
        <ConfirmModal
          title={t('ProfileMenu.modal_title')}
          subtitle={t('ProfileMenu.modal_subtitle')}
          confirmLabel={t('ProfileMenu.signout_button_label')}
          cancelLabel={t('ProfileMenu.cancel_button_label')}
          onConfirm={handleSignout}
          onCancel={handleToggleModal}
        />
      )}
    </>
  );
};
